import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { ActivityStep } from '@models/activity.model';
import { LinePlan } from '@models/asset-details.model';
import {
  createShoppingObject,
  createShoppingPriceObject,
  stringOnlyHTML
} from '@models/functions.model';
import { CartInfo } from '@models/session-details';
import { InCartObjects, ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  ADMIN_OPTIONS,
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  ColorOption,
  ColorsListResponse,
  DeviceCarriersListResponse,
  DeviceData,
  DeviceItemCarrier,
  DeviceItemData,
  DeviceItemDevice,
  DeviceItemDeviceBP,
  DevicePrice,
  FeatureType,
  PlanType,
  ShoppingDataInitialLoad,
  ShoppingFilterItem,
  StoreQueryParams
} from '@models/shopping.model';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Subject, Subscription, asapScheduler, finalize, takeUntil } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  DataLoaderService
} from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../admin-override/admin-override-badge/admin-override-badge.component';
import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';
import { CarrierComponent } from '../customize-device/carrier/carrier.component';
import { DeviceDescriptionComponent } from '../customize-device/device-description/device-description.component';
import { ImagePlaceholderComponent } from '../customize-device/image-placeholder/image-placeholder.component';

@Component({
  selector: 'ts-global-device-details',
  templateUrl: './global-device-details.component.html',
  providers: [NgbActiveModal],
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    StopClickPropagationDirective,
    AdminOverrideBadgeComponent,
    AdminOverrideBannerComponent,
    CarrierComponent,
    BaseBadgeComponent,
    DeviceDescriptionComponent,
    ImagePlaceholderComponent,
    PriceSummaryComponent
  ]
})
export class GlobalDeviceDetailsComponent implements OnInit, OnDestroy {
  @ContentChild(TemplateRef) customizableOptionsTemplate: TemplateRef<any>;
  @Output() carrierChanged: EventEmitter<void> = new EventEmitter<void>();
  current_activity_id: number;
  selected_device_manufacturer: string;
  selected_device_model: string;
  selected_device_name: string | null;
  selected_device_id: number;
  selected_carrier_id: number;
  selected_normalized_color: string;
  selected_device_size: string;
  selected_storage_size: string;
  selected_plan_id: number | undefined;
  selected_feature_list: FeatureType[] = [];
  selected_storage_device_price: DevicePrice = {
    onetime: { cost_to_currency: '' },
    monthly: { cost_to_currency: '' }
  };
  selected_storage_device_id: number = -1;
  selected_color_device_id: number = -1;
  selected_name_override_id: number = -1;
  storage_auto_select_needed: boolean = false;
  storage_reselect_needed: boolean = false;
  name_override_restore_id: number = -1;
  // Marks the reload triggered by an admin toggle. selectStorage consumes it to skip
  // resetPlanSelection for that one cascade: the reload is not a user-initiated storage
  // change, so the plan and features it would discard must survive.
  preserve_plan_selection_on_reload: boolean = false;

  device_data: DeviceData;
  device_record: DeviceItemData[];
  is_bp_device: boolean | undefined;
  active_device: DeviceItemDevice | DeviceItemDeviceBP | null = null;
  plan_list: PlanType[] = [];
  feature_list: FeatureType[] = [];
  no_plans: boolean = false;
  no_features: boolean = false;
  keep_plan: boolean = false;
  is_display_one_time_cost: boolean = false;
  is_display_recurring_cost: boolean = false;
  existing_image_url: string | undefined;

  accessories_enabled: boolean = true;
  url_filters_param: string;
  devices_payload: any = {
    filters: [],
    orderTypeId: 0,
    categories: [],
    countryId: 0,
    include_admin_options: undefined,
    isDeviceItemRequest: true
  };
  device_plan: ShoppingDataInitialLoad = {
    categories: [],
    filters: [],
    orderTypeId: 0,
    deviceId: null
  };
  shopping_cart: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  in_cart_device: DeviceItemDevice | null;
  revisiting: boolean = false;
  pending_feature_deletes: number[] = [];
  admin_override_persist: boolean = false;
  has_entered_admin_mode: boolean = false;
  modal_dismissed: boolean = false;
  progress_bar_config: ActivityStep[];
  refresh_summary: Subject<boolean | string> = new Subject<boolean | string>();
  plan_features_sub: Subject<void> = new Subject<void>();
  plan_feature_memory: { [planId: string]: string[] } = {};
  private subscriptions: Subscription = new Subscription();
  destroy$: Subject<void> = new Subject<void>();

  // TODO sort these new properties
  carrier_list: DeviceItemCarrier[] = [];
  load_storage_list_post_check: boolean = false;
  set_name_override_id: boolean = false;
  forward_backward_logic: boolean = false;
  load_color_list: boolean = false;
  reset_list_options: boolean = false;
  options_disable_check: boolean = false;
  has_storage_list: boolean = false;

  constructor(
    private activated_route: ActivatedRoute,
    private activity_state_svc: ActivityStateService,
    private data_loader_svc: DataLoaderService,
    private modal_svc: ModalService,
    private router: Router,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    public cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.current_activity_id =
      this.device_plan.orderTypeId =
      this.devices_payload.orderTypeId =
      this.shopping_cart.orderTypeId =
      this.shopping_items_price.orderTypeId =
        this.activity_state_svc.orderTypeId()!;

    this.shopping_cart.lineId = this.shopping_items_price.lineId =
      this.activity_state_svc.parameter('lineId');

    this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
      this.activity_state_svc.parameter('device_include_admin_options');
    this.shopping_items_price.device_include_admin_options =
      this.shopping_cart.device_include_admin_options;

    this.subscriptions.add(
      this.activated_route.queryParamMap.subscribe({
        next: (paramMap) => {
          this.selected_carrier_id = Number(paramMap.get('carrierId'));
          this.selected_device_id = Number(paramMap.get('deviceId')); // not for all activity types
          this.selected_device_name = paramMap.get('name');
          this.devices_payload.keep_plan = this.keep_plan = paramMap.get('keep_plan') === 'true';
          this.devices_payload.categories = JSON.parse(paramMap.get('categories') as string);
          this.devices_payload.countryId = this.activity_state_svc.parameter('country')?.id;
          this.is_bp_device = paramMap.get('isBpDevice') === 'true';

          if ([3].includes(this.current_activity_id) || this.is_bp_device) {
            this.url_filters_param = paramMap.get('filters') as string;
            this.devices_payload.filters = JSON.parse(paramMap.get('filters') as string);
            this.selected_device_manufacturer = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'manufacturer'
            )[0].value;
            this.selected_device_model = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'model'
            )[0].value;
            this.selected_device_size = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'size'
            )[0]?.value;
            this.selected_normalized_color = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'color'
            )[0].value;
          } else {
            const filter: ShoppingFilterItem = {
              id: Number(paramMap.get('filterConfigId')),
              value: this.selected_device_name!
            };
            if (
              this.devices_payload.filters.every(
                (f: ShoppingFilterItem) => f.id !== filter.id && f.value !== filter.value
              )
            )
              this.devices_payload.filters.push(filter);
          }
          this.seedSelectionsFromCart(CartInfo.cart_data);
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
    this.loadDevice();
    this.data_loader_svc.toggle(false);
  }

  ngOnDestroy(): void {
    // Abandon path: the user changed plan and left via the stepper instead of pressing
    // Continue, so continueSucceeded never ran. Without this the orphaned features stay in the
    // backend cart and are restored on the next revisit.
    if (this.pending_feature_deletes.length) {
      this.shopping_svc.clearSpecifiedCartItems(this.pending_feature_deletes, false).subscribe({
        error: () => {}
      });
      this.pending_feature_deletes = [];
    }

    this.plan_features_sub.next();
    this.plan_features_sub.complete();
    this.shopping_svc.preselected_item = false;
  }

  get page_to_navigate(): string {
    return this.accessories_enabled
      ? `shop/${this.activity_state_svc.orderTypeId()}/accessories`
      : `shop/${this.activity_state_svc.orderTypeId()}/finalize-order`;
  }

  get content_loading(): boolean {
    return (
      this.data_loader_svc.isLoading('device-plans') ||
      this.data_loader_svc.isLoading('device-plan-features')
    );
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get optionsContext(): { global: GlobalDeviceDetailsComponent } {
    return { global: this };
  }

  get disable_admin_overide(): boolean {
    return this.device_data?.admin_options === ADMIN_OPTIONS.ALL_ADMIN_OPTIONS;
  }

  get admin_cart_items(): boolean {
    return (
      CartInfo.cart_data?.shopping_cart_items?.some(
        (item: ShoppingCartItem) =>
          item.type !== CART_ITEM_TYPE_ACCESSORY && item.additional_info?.admin === true
      ) ?? false
    );
  }

  get disable_continue_btn(): boolean {
    const cart_params: string[] = [
      CART_ITEM_TYPE_PLAN,
      ...(this.shopping_cart.orderTypeId !== 3 ? [CART_ITEM_TYPE_DEVICE] : [])
    ];
    const cart_params_normalized: string[] = cart_params.map((required_type: string) =>
      required_type.toLowerCase()
    );
    const cart_types = this.shopping_cart.shopping_cart_items.map((item: ShoppingCartItem) =>
      (item.type as string)?.toLowerCase()
    );

    let valid_cart: boolean = cart_params_normalized.every((required_type: string) =>
      cart_types.includes(required_type)
    );

    if (this.options_disable_check)
      valid_cart = valid_cart && this.selected_name_override_id !== -1;

    if (this.keep_plan) return !(valid_cart && this.keep_plan && Boolean(this.plan_list.length));

    return !valid_cart;
  }

  /**
   * The cart write committed, so the features orphaned by a plan change can now be deleted.
   *
   * Order type 3 blanks the price summary's navigate_page (see the template) and navigates from
   * here instead, once the DELETE has settled, so the next page can never read a cart that
   * still holds the old plan's features. Other order types keep the price summary's own
   * navigation and never queue anything, so this is a no-op for them.
   */
  continueSucceeded(): void {
    if (![3].includes(this.current_activity_id)) return;

    const queued_ids: number[] = [...this.pending_feature_deletes];
    this.pending_feature_deletes = [];

    // The local cart is exactly what the PUT just committed. A feature offered by both the old
    // and the new plan can be queued for deletion and re-selected under the new plan; deleting
    // it here would strip it straight back out of the cart the user just committed.
    const committed_feature_ids: Set<string> = new Set(
      this.shopping_cart.shopping_cart_items
        .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
        .map((item: ShoppingCartItem) => String(item.id))
    );
    const ids_to_delete: number[] = queued_ids.filter(
      (id: number) => !committed_feature_ids.has(String(id))
    );

    if (!ids_to_delete.length) {
      this.navigateToNextPage();
      return;
    }

    // finalize, not next: a failed delete must still let the user move on.
    this.shopping_svc
      .clearSpecifiedCartItems(ids_to_delete, false)
      .pipe(finalize(() => this.navigateToNextPage()))
      .subscribe({ error: () => {} });
  }

  disableContinueBtnCheck($event: boolean): void {
    this.options_disable_check = $event;
  }

  hasStorageList($event: boolean): void {
    this.has_storage_list = $event;
  }

  sanitizedHTML = (description: string | undefined): boolean =>
    !stringOnlyHTML(description) && description !== '';

  appendMonthlyLabel = (cost: string): string => (cost.match(/mo(nth)?$/i) ? cost : `${cost}/mo`);

  getItemByType = (type: string): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.type === type);

  getItemById = (id: string | number): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.id == id) ??
    CartInfo.cart_data?.shopping_cart_items?.find((item: ShoppingCartItem) => item.id == id);

  getActiveFeatures(feature_details: FeatureType): boolean {
    if ((this.selected_plan_id && feature_details.included) || this.keep_plan) return true;
    else
      return !!this.selected_feature_list?.find(
        (feature: FeatureType) => feature.id == feature_details.id
      );
  }

  confirmAdminToggleClearCart(admin_override: boolean): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          let cart_items_to_delete: ShoppingCartItem[] = res.shopping_cart_items.filter(
            (cart_item: ShoppingCartItem) => cart_item.type !== CART_ITEM_TYPE_ACCESSORY
          );
          this.confirmChangesModal(
            () => this.getAdminOverride(admin_override, true),
            cart_items_to_delete
          );
        }
      })
    );
  }

  getAdminOverride(admin_override: boolean, clear_cart: boolean = false): void {
    if (!this.disable_admin_overide) {
      this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
        admin_override;
      this.shopping_items_price.device_include_admin_options = admin_override;

      const refresh_lists_on_toggle: boolean = [1, 1001].includes(this.current_activity_id);
      const has_admin_selection: boolean = this.shopping_cart.shopping_cart_items.some(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN && item.additional_info?.admin
      );
      const is_device_admin: boolean =
        !!this.getItemByType(CART_ITEM_TYPE_DEVICE)?.additional_info?.admin;
      const must_clear_selection: boolean = refresh_lists_on_toggle
        ? clear_cart || (is_device_admin && !admin_override)
        : is_device_admin || clear_cart || (admin_override && !this.has_entered_admin_mode);

      if (this.selected_name_override_id !== -1)
        this.name_override_restore_id = this.selected_name_override_id;

      const preserved_storage_size: string = this.selected_storage_size;

      // Re-established on every toggle so a value left over from a previous cascade can never
      // accumulate. A confirmed cart clear genuinely wants the full reset, so it stays false.
      this.preserve_plan_selection_on_reload = !clear_cart;

      if (must_clear_selection) {
        this.selected_storage_device_id = -1;
        this.selected_name_override_id = -1;

        if (clear_cart) this.selected_storage_size = '';

        this.storage_auto_select_needed = clear_cart;
        this.storage_reselect_needed = !clear_cart;
      } else {
        this.storage_reselect_needed = true;
        this.storage_auto_select_needed = false;
      }

      if (!admin_override && (is_device_admin || clear_cart)) {
        this.resetPlansAndFeatures();
        this.has_entered_admin_mode = false;

        if (!clear_cart) this.selected_storage_size = preserved_storage_size;
      } else if (!admin_override) {
        if (has_admin_selection && !this.keep_plan) {
          this.plan_list = this.feature_list = this.selected_feature_list = [];
          this.selected_plan_id = 0;
          this.no_plans = this.no_features = false;
          this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
            (item: ShoppingCartItem) =>
              item.type !== CART_ITEM_TYPE_PLAN && item.type !== CART_ITEM_TYPE_FEATURE
          );
          this.shopping_items_price.shopping_cart_items =
            this.shopping_items_price.shopping_cart_items.filter(
              (item: ShoppingCartItem) =>
                item.type !== CART_ITEM_TYPE_PLAN && item.type !== CART_ITEM_TYPE_FEATURE
            );
        } else if (!this.keep_plan) {
          // Scenario 1: non-admin plan — retain plan and non-admin features, drop admin features
          const isNonAdminFeature = (item: ShoppingCartItem): boolean =>
            item.type !== CART_ITEM_TYPE_FEATURE || !item.additional_info?.admin;
          this.shopping_cart.shopping_cart_items =
            this.shopping_cart.shopping_cart_items.filter(isNonAdminFeature);
          this.shopping_items_price.shopping_cart_items =
            this.shopping_items_price.shopping_cart_items.filter(isNonAdminFeature);
          if (CartInfo.cart_data) {
            CartInfo.cart_data = {
              ...CartInfo.cart_data,
              shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(isNonAdminFeature)
            };
          }
          this.selected_feature_list = this.selected_feature_list.filter(
            (feature: FeatureType) => !feature.admin
          );
          if (refresh_lists_on_toggle) {
            this.plan_list = this.feature_list = [];
            this.no_plans = this.no_features = false;
          } else {
            this.feature_list = this.feature_list.filter((feature: FeatureType) => !feature.admin);
          }
        }
      } else if (!this.keep_plan) {
        if (refresh_lists_on_toggle) {
          this.plan_list = this.feature_list = [];
          this.no_plans = this.no_features = false;
        } else if (must_clear_selection) {
          // Drop only the cached lists so they reload with the admin-inclusive catalog, exactly
          // as the refresh_lists_on_toggle branch above does. The selection itself is end-user
          // and must survive: clearing selected_plan_id and the cart PLAN/FEATURE items here
          // discarded the user's features, and left selected_plan_id at 0 for the features
          // request to pick up. It also emptied the cart before the admin_override_persist
          // check below, so the restore branch in selectNameOverride never ran.
          this.plan_list = this.feature_list = [];
          this.no_plans = this.no_features = false;
          // Remove any local-only device so the storage auto-select condition
          // (!getItemByType(DEVICE)) passes downstream. If a real backend device
          // exists, in_cart_device will restore it via loadStorageList.
          this.removeItems(CART_ITEM_TYPE_DEVICE);
        }
      }

      if (
        this.shopping_cart?.shopping_cart_items.find(
          (item: ShoppingCartItem) =>
            item.type === CART_ITEM_TYPE_PLAN || item.type === CART_ITEM_TYPE_FEATURE
        )
      )
        this.admin_override_persist = true;

      if (admin_override) this.has_entered_admin_mode = true;

      if (refresh_lists_on_toggle && this.selected_plan_id && this.selected_feature_list.length)
        this.plan_feature_memory[String(this.selected_plan_id)] = this.selected_feature_list.map(
          (feature: FeatureType) => String(feature.id)
        );

      if ([3].includes(this.current_activity_id)) this.load_color_list = false;

      // loadDevice triggers loadCarrierList → selectCarrier → load_color_list toggle →
      // the full color → storage → nameOverrides → plans → features cascade, which is the
      // single authoritative path for all API calls after an admin toggle.
      this.loadDevice(() => {
        this.getShoppingCartData(this.shopping_cart, true, true);
        this.refresh_summary.next(true);
      });
    }
  }

  selectCarrierClick(device_carrier: DeviceItemCarrier): void {
    if (this.revisiting && device_carrier.id != this.selected_carrier_id && !this.modal_dismissed) {
      this.confirmChangesModal(() => this.selectCarrier(device_carrier));
    } else this.selectCarrier(device_carrier);
  }

  /**
   * @param user_initiated true only when the user clicks a plan card. Restoration paths
   * (loadDevicePlanData, displayOldPlanAndFeatures, keepPlansAndFeatures) leave it false so
   * they can never mutate the backend cart while rebuilding display state on revisit.
   */
  selectPlan(plan_details: PlanType, user_initiated: boolean = false): void {
    const same_plan: boolean = String(this.selected_plan_id) == String(plan_details.id);
    if (same_plan && !this.keep_plan && this.feature_list.length) return;

    // Save current plan's feature selections before clearing, so switching back restores them.
    // Read from selected_feature_list (always accurate) rather than CartInfo (can be stale).
    if (!same_plan && this.selected_plan_id) {
      this.plan_feature_memory[String(this.selected_plan_id)] = this.selected_feature_list.map(
        (f: FeatureType) => String(f.id)
      );

      // The user actively swapped the plan, so the previous plan's features have to leave the
      // backend cart as well. Must run before the CartInfo cleanup below, which is where the
      // stale ids are read from. The delete itself waits for Continue.
      if (user_initiated) this.queueStaleBackendFeatures();
    }

    const retained_feature_ids: Array<string | number> = same_plan
      ? this.shopping_cart.shopping_cart_items
          .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
          .map((item: ShoppingCartItem) => item.id)
      : [];

    this.removeItems(CART_ITEM_TYPE_PLAN);
    if (!this.keep_plan) this.removeItems(CART_ITEM_TYPE_FEATURE);
    this.plan_features_sub.next();

    // CartInfo is the record of what the backend cart still holds, and the header badge reads
    // it directly (header-bar.getCartQuantity). On revisit it must survive an active plan
    // change untouched: the queued features are not deleted until Continue, so clearing it
    // here would empty the badge while the backend still holds those items.
    if (!same_plan && CartInfo.cart_data && !this.revisiting) {
      CartInfo.cart_data = {
        ...CartInfo.cart_data,
        shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
          (item: ShoppingCartItem) =>
            item.type !== CART_ITEM_TYPE_PLAN && item.type !== CART_ITEM_TYPE_FEATURE
        )
      };
    }

    this.is_display_recurring_cost = true;
    this.selected_plan_id = plan_details.id;

    if (!this.keep_plan)
      this.loadFeatureList(
        plan_details.id,
        retained_feature_ids.length ? retained_feature_ids : undefined
      );
    this.generateShoppingItems(plan_details, CART_ITEM_TYPE_PLAN, true);
  }

  selectFeature(feature_details: FeatureType): void {
    const selected_features: boolean = !!this.selected_feature_list.find(
      (feature: FeatureType) => feature.id == feature_details.id
    );
    // A feature queued for deletion is still present in CartInfo (the badge reads it), but it
    // is on its way out of the backend cart, so it must not count as "already in cart" — the
    // local cart is what Continue sends, and skipping the add would drop it from the payload.
    const feature_pending_delete: boolean = this.pending_feature_deletes.some(
      (id: number) => String(id) === String(feature_details.id)
    );
    const feature_in_cart: boolean =
      !!this.getItemById(feature_details.id) && !feature_pending_delete;

    if (selected_features) {
      // User is deselecting the feature
      this.selected_feature_list = this.selected_feature_list.filter(
        (feature: FeatureType) => feature.id != feature_details.id
      );

      // Only remove from cart if it's not included and not keep_plan
      if (feature_in_cart && !feature_details.included && !this.keep_plan) {
        this.shopping_svc.clearSpecifiedCartItems([feature_details.id], false).subscribe({
          next: () => {
            this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
              (item: ShoppingCartItem) => item.id != feature_details.id
            );
            this.shopping_items_price.shopping_cart_items =
              this.shopping_items_price.shopping_cart_items.filter(
                (item: ShoppingCartItem) => item.id != feature_details.id
              );
            if (CartInfo.cart_data) {
              CartInfo.cart_data = {
                ...CartInfo.cart_data,
                shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
                  (item: ShoppingCartItem) => item.id != feature_details.id
                )
              };
            }
            this.refresh_summary.next(true);
          },
          error: () => {
            this.selected_feature_list.push(feature_details);
          }
        });
      }
    } else {
      // User is selecting the feature
      this.selected_feature_list.push(feature_details);
      // Only add to cart if not already there
      if (!feature_in_cart)
        this.generateShoppingItems(feature_details, CART_ITEM_TYPE_FEATURE, true);
    }
  }

  getShoppingCartData(
    shopping_cart: ShoppingCartData,
    device_refreshed: boolean = false,
    skip_storage_restoration: boolean = false
  ): void {
    if (shopping_cart) {
      this.setCartValues(shopping_cart);

      if (!device_refreshed) {
        this.revisiting = ![3].includes(this.current_activity_id)
          ? shopping_cart.shopping_cart_items.filter(
              (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
            ).length > 0
          : shopping_cart.shopping_cart_items.filter(
              (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
            ).length > 0;

        this.selected_device_id =
          shopping_cart.existing_asset?.device?.id ?? this.selected_device_id;
      }

      if ([2].includes(this.current_activity_id)) this.displayOldPlanAndFeatures(shopping_cart);
      else {
        shopping_cart.shopping_cart_items.forEach((item: ShoppingCartItem) => {
          const alreadyInCart: boolean = this.shopping_cart.shopping_cart_items.some(
            (local_item: ShoppingCartItem) =>
              local_item.id == item.id && local_item.type === item.type
          );
          if (!alreadyInCart)
            this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id);
        });
      }

      const cart_objects: InCartObjects = this.retrieveCartObjects();

      // Skip storage restoration when admin toggle changes to allow auto-select to work
      if ((cart_objects.device_id || cart_objects.plan_id) && !skip_storage_restoration) {
        if (this.in_cart_device) {
          if (![1001].includes(this.activity_state_svc.orderTypeId())) {
            this.selected_storage_device_id = this.in_cart_device.id;
            this.set_name_override_id = true;
            this.selected_device_id = this.in_cart_device.id;
          }
        } else if (cart_objects.device_id)
          this.selected_storage_device_id = this.selected_device_id = cart_objects.device_id;
        this.forward_backward_logic = true;
      }

      this.is_display_one_time_cost =
        this.keep_plan ||
        this.revisiting ||
        this.selected_storage_device_id !== -1 ||
        this.shopping_cart.shopping_cart_items.find(
          (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
        )
          ? true
          : false;

      this.is_display_recurring_cost =
        this.keep_plan || this.revisiting || this.selected_plan_id || this.active_device
          ? true
          : false;

      this.setCartValues(shopping_cart);
      this.syncPriceItemsWithCart();

      // Trigger price recalculation after cart restoration
      if (this.shopping_cart.shopping_cart_items.length > 0) this.refresh_summary.next(true);
    }
  }

  openDetailsModal(obj: PlanType | FeatureType, body: string): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = obj.name_override ? obj.name_override : obj.name;
    modalRef.componentInstance.modal_body = body;
  }

  private loadDevice(callback?: Function): void {
    this.subscriptions.add(
      this.shopping_svc.getDevices(this.devices_payload, 'global').subscribe({
        next: (res: DeviceData) => {
          if (res.items.length) {
            this.device_data = res;
            this.accessories_enabled = res.progress_bar_config.is_accessory_ordering_enabled;
            this.progress_bar_config = res.progress_bar_config.steps;
            this.device_record = this.is_bp_device
              ? this.setBpDeviceRecord(res.items as DeviceItemData[])
              : (res.items as DeviceItemData[]);
            this.in_cart_device = res.in_cart_device;

            if (this.in_cart_device) {
              const device: DeviceItemDevice = this.in_cart_device;
              // For Order Type 3: Don't set revisiting=true if we're in the middle of a carrier change
              // The carrier change flow will handle state appropriately
              if (![3].includes(this.current_activity_id)) this.revisiting = true;

              this.selected_color_device_id = device.id;
              this.selected_normalized_color = device.original_color || device.color;
              this.selected_storage_device_id = device.id;
              this.selected_storage_size = device.size;
              this.selected_name_override_id = device.id;
              this.active_device = device; // Set initial active_device for template rendering
            } else if (this.device_record?.length && this.device_record[0]?.devices?.length) {
              // The colour-list API determines the initial displayed variant. Do not assign the
              // first raw device response here, because it can briefly show a different colour
              // and image before DeviceDetailsOptionsComponent selects the colour-list result.
              this.active_device = null;
            }

            this.loadCarrierList();

            if (this.device_record && callback) asapScheduler.schedule(() => callback());
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private setBpDeviceRecord(items: DeviceItemData[]): DeviceItemData[] {
    let bp_device_record: DeviceItemData[] = [];
    items.forEach((item: DeviceItemData) => {
      item.devices.forEach((device: DeviceItemDevice) => {
        if (device.id === this.selected_device_id) {
          item.devices = [device];
          bp_device_record = [item];
          return;
        }
      });
    });
    return bp_device_record;
  }

  private loadCarrierList(): void {
    this.shopping_svc.getCarriersList(this.devices_payload).subscribe({
      next: (res: DeviceCarriersListResponse) => {
        this.carrier_list = res.carriers.map((c) => ({ ...c, id: Number(c.id) }));

        if (this.carrier_list.length === 1) this.selectCarrier(this.carrier_list[0], true);
        else if (this.carrier_list.length > 1)
          this.selectCarrier(
            this.carrier_list.find(
              (carrier: DeviceItemCarrier) =>
                Number(carrier.id) === Number(this.selected_carrier_id)
            ) ?? this.carrier_list[0],
            true
          );

        if (!this.in_cart_device) this.resolveInitialColor();
      },
      error: (e: HttpErrorResponse) => {}
    });
  }

  private resolveInitialColor(): void {
    this.shopping_svc
      .getColorsList(
        {
          ...this.devices_payload,
          business_purpose: this.is_bp_device
            ? (this.device_record?.[0]?.devices?.[0] as DeviceItemDeviceBP)?.business_purpose
            : undefined
        },
        this.selected_carrier_id
      )
      .subscribe({
        next: (res: ColorsListResponse) => {
          const normalizedColor = this.selected_normalized_color?.toLowerCase().trim() ?? '';
          const selectedColor: ColorOption | undefined =
            res.items.find((color: ColorOption) => color.id === this.selected_color_device_id) ??
            res.items.find(
              (color: ColorOption) =>
                !!normalizedColor &&
                (color.color?.toLowerCase().trim() === normalizedColor ||
                  color.original_color?.toLowerCase().trim() === normalizedColor)
            ) ??
            res.items[0];

          if (!selectedColor) return;

          this.active_device = selectedColor;
          this.selected_device_id = this.selected_color_device_id = selectedColor.id;
          this.selected_normalized_color = selectedColor.color;
          this.load_color_list = true;
        },
        error: (err: HttpErrorResponse) => {
          console.error('resolveInitialColor: getColorsList failed', err);
          // fallback: show first raw device to avoid blank state
          if (this.device_record?.[0]?.devices?.[0]) {
            this.active_device = this.device_record[0].devices[0];
            this.load_color_list = true;
          }
        }
      });
  }

  private loadDevicePlanData(device_id: number, callback?: Function): void {
    this.plan_features_sub.next();
    const param: { [key: string]: number | string | boolean } = {
      deviceId: device_id
    };
    this.device_plan.deviceId = device_id;
    this.plan_list = [];

    if ([3].includes(this.current_activity_id)) this.has_storage_list = true;

    if (this.shopping_cart?.device_include_admin_options !== undefined)
      param['include_admin_options'] = this.shopping_cart.device_include_admin_options;

    this.shopping_svc
      .getPlans(this.selected_carrier_id, param)
      .pipe(takeUntil(this.plan_features_sub))
      .subscribe({
        next: (res: { plans: PlanType[] }) => {
          this.plan_list = res.plans;
          this.no_plans = !res.plans.length;

          let cart_plan: ShoppingCartItem | undefined =
            this.getItemByType(CART_ITEM_TYPE_PLAN) ??
            CartInfo.cart_data?.shopping_cart_items?.find(
              (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
            );
          let cart_plan_in_list: PlanType | undefined = this.plan_list.find(
            (plan: PlanType) => plan.id == cart_plan?.id
          );

          if (!cart_plan_in_list && this.plan_list.length === 1) this.selectPlan(this.plan_list[0]);
          else if (cart_plan) {
            if (cart_plan_in_list && !callback) this.selectPlan(cart_plan_in_list);
            else if (cart_plan_in_list && callback) {
              // Forward/backward restore path can arrive with selected_plan_id set but
              // missing local PLAN item in pricing payload; rehydrate it before restoring features.
              if (!this.getItemByType(CART_ITEM_TYPE_PLAN))
                this.generateShoppingItems(cart_plan_in_list, CART_ITEM_TYPE_PLAN, false);
              else if (
                !this.shopping_items_price.shopping_cart_items.some(
                  (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
                )
              )
                this.shopping_items_price.shopping_cart_items.push({
                  type: CART_ITEM_TYPE_PLAN,
                  id: cart_plan_in_list.id,
                  quantity: 1
                } as ShoppingCartItem);

              asapScheduler.schedule(() => callback());
            } else {
              this.removeItems(CART_ITEM_TYPE_PLAN);
              this.removeItems(CART_ITEM_TYPE_FEATURE);
              this.refresh_summary.next(true);
            }
          }

          this.createPayloadString();
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  private loadFeatureList(
    plan_id: number,
    restore_feature_ids?: Array<string | number>,
    force_refresh: boolean = false
  ): void {
    let cart_updated: boolean = false;
    const param: { [key: string]: number | string | boolean } = {
      deviceId: this.device_plan.deviceId!,
      planId: plan_id
    };

    // Capture cart feature IDs from backend cart (CartInfo.cart_data)
    // Use backend cart because selectPlan() clears local cart before calling this method.
    // Only restore features when the plan in CartInfo matches the plan being loaded — this
    // prevents features from a previously-selected plan from leaking into a new plan.
    const cart_plan_id = CartInfo.cart_data?.shopping_cart_items?.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
    )?.id;
    const cartFeatureIds: Set<string> = new Set([
      ...(String(cart_plan_id) === String(plan_id)
        ? (CartInfo.cart_data?.shopping_cart_items ?? [])
            .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
            .map((item: ShoppingCartItem) => String(item.id))
        : []),
      ...(this.plan_feature_memory[String(plan_id)] ?? [])
    ]);

    // Clear both lists to start fresh
    this.feature_list = this.selected_feature_list = [];

    if (this.shopping_cart?.device_include_admin_options !== undefined) {
      param['include_admin_options'] = this.shopping_cart.device_include_admin_options;
    }

    this.shopping_svc
      .getFeatures(this.selected_carrier_id, param)
      .pipe(takeUntil(this.plan_features_sub))
      .subscribe({
        next: (res: { features: FeatureType[] }) => {
          this.feature_list = res.features.sort((a, b) => (a.included > b.included ? -1 : 1));
          this.no_features = !res.features.length;

          const features_to_auto_select: FeatureType[] = this.feature_list.filter(
            (feature: FeatureType) => feature.included || cartFeatureIds.has(String(feature.id))
          );

          // Auto-select features and add them to cart
          this.selected_feature_list = features_to_auto_select;

          // Add auto-selected features to cart for price calculation.
          // Skip features already in cart — the callback restore path skips selectPlan,
          // so features may still be present. Calling generateShoppingItems on an existing
          // non-included feature would toggle-remove it, causing a price drop.
          features_to_auto_select.forEach((feature: FeatureType) => {
            const already_in_cart: boolean = this.shopping_cart.shopping_cart_items.some(
              (item: ShoppingCartItem) =>
                item.id == feature.id && item.type === CART_ITEM_TYPE_FEATURE
            );
            if (!already_in_cart) {
              this.generateShoppingItems(feature, CART_ITEM_TYPE_FEATURE, false);
              cart_updated = true;
            }
          });

          const cart_feature_ids: string[] = this.shopping_cart.shopping_cart_items
            .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
            .map((item: ShoppingCartItem) => String(item.id));
          const restore_ids: string[] = restore_feature_ids
            ? restore_feature_ids.map((id) => String(id))
            : cart_feature_ids;
          restore_ids
            .filter(
              (id: string) =>
                !this.feature_list.some((feature: FeatureType) => String(feature.id) === id)
            )
            .forEach((id: string) => {
              const cart_item =
                this.shopping_cart.shopping_cart_items.find(
                  (item: ShoppingCartItem) => String(item.id) === id
                ) ??
                CartInfo.cart_data?.shopping_cart_items?.find(
                  (item: ShoppingCartItem) => String(item.id) === id
                );
              if (cart_item) {
                this.feature_list.push({
                  id: Number(cart_item.id),
                  feature_id: Number(cart_item.id),
                  name: cart_item.name ?? cart_item.additional_info?.option ?? '',
                  name_override: cart_item.additional_info?.option ?? cart_item.name ?? '',
                  category: cart_item.additional_info?.category ?? '',
                  description: cart_item.description ?? '',
                  cost_to_currency:
                    cart_item.monthly_cost_to_currency ??
                    cart_item.cost_to_currency ??
                    cart_item.onetime_cost_to_currency ??
                    '',
                  included: false,
                  unchanged: Boolean(cart_item.additional_info?.unchanged),
                  admin: cart_item.additional_info?.admin
                } as FeatureType);
              }
            });

          this.feature_list.forEach((feature: FeatureType) => {
            const shouldRestore: boolean =
              !feature.included &&
              restore_ids.includes(String(feature.id)) &&
              !this.selected_feature_list.find((f: FeatureType) => f.id === feature.id);
            if (shouldRestore) {
              // Check if feature is in local shopping_cart (not just CartInfo.cart_data)
              const feature_in_local_cart: boolean = !!this.shopping_cart.shopping_cart_items.find(
                (item: ShoppingCartItem) =>
                  item.id == feature.id && item.type === CART_ITEM_TYPE_FEATURE
              );
              const cart_data_feature: ShoppingCartItem | undefined =
                CartInfo.cart_data?.shopping_cart_items?.find(
                  (item: ShoppingCartItem) =>
                    item.id == feature.id && item.type === CART_ITEM_TYPE_FEATURE
                );

              if (feature_in_local_cart) this.selected_feature_list.push(feature);
              else if (cart_data_feature) {
                // Feature is in CartInfo.cart_data but not in local cart - restore it directly

                this.shopping_cart.shopping_cart_items.push(cart_data_feature);
                this.shopping_items_price.shopping_cart_items.push({
                  type: cart_data_feature.type,
                  id: cart_data_feature.id,
                  quantity: cart_data_feature.quantity,
                  sub_type: cart_data_feature.sub_type
                });
                this.selected_feature_list.push(feature);
                cart_updated = true;
              } else this.selectFeature(feature);
            }
          });

          this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.reduce(
            (acc: ShoppingCartItem[], item: ShoppingCartItem) => {
              if (item.type === CART_ITEM_TYPE_FEATURE) {
                const features_present: boolean = this.feature_list.some(
                  (feature: FeatureType) => feature.id == item.id
                );
                if (!features_present && restore_ids.includes(String(item.id)))
                  return [...acc, item];
                if (!features_present) {
                  this.selected_feature_list = this.selected_feature_list.filter(
                    (feature: FeatureType) => feature.id != item.id
                  );
                  cart_updated = true;
                  return acc;
                }
              }
              return [...acc, item];
            },
            []
          );

          // Keep price payload aligned with local cart after feature restore/cleanup.
          this.syncPriceItemsWithCart();

          if (cart_updated || force_refresh) this.refresh_summary.next(true);
          this.createPayloadString();
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  /**
   * Queues the previously selected plan's features for deletion from the backend cart.
   *
   * Order types 2/3/4/5 persist the cart with PUT (see the reset_cart binding in the template
   * and sendShoppingItems): items dropped from the payload are NOT removed server-side, so a
   * plan change leaves the old features orphaned unless they are deleted explicitly. The PLAN
   * itself is upserted by type and needs no delete. Order types that POST (1, 1001) replace the
   * whole cart on Continue and are deliberately left untouched.
   *
   * Queued rather than deleted here so the cart count holds its committed value until the user
   * presses Continue, matching how adding a feature behaves. continueSucceeded() issues it.
   */
  private queueStaleBackendFeatures(): void {
    if (![2, 3, 4, 5].includes(this.current_activity_id) || this.keep_plan) return;

    // CartInfo is the record of what the backend holds, so features added under the current
    // plan but not yet written are correctly absent and never queued.
    const stale_feature_ids: number[] = (CartInfo.cart_data?.shopping_cart_items ?? [])
      .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
      .map((item: ShoppingCartItem) => Number(item.id));

    this.pending_feature_deletes = [
      ...new Set([...this.pending_feature_deletes, ...stale_feature_ids])
    ];
  }

  /**
   * Mirrors PriceSummaryComponent.navigateToNextPage, which is suppressed for order type 3 by
   * binding an empty navigate_page. Keep the query params in step with that method.
   */
  private navigateToNextPage(): void {
    this.router.navigate([this.page_to_navigate], {
      queryParams: { deviceId: this.device_plan.deviceId, keep_plan: this.keep_plan }
    });
  }

  private confirmChangesModal(callback: () => void, admin_cart_items?: ShoppingCartItem[]): void {
    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.getTranslatedModalLabels()[0],
        message: this.translation_svc.getTranslatedModalLabels()[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          const items_to_delete: number[] = (
            admin_cart_items ?? this.shopping_cart.shopping_cart_items
          )
            .filter((item: ShoppingCartItem) => item.type !== CART_ITEM_TYPE_ACCESSORY)
            .map((item: ShoppingCartItem) => item.id as number);

          this.shopping_svc.clearSpecifiedCartItems(items_to_delete, false).subscribe({
            next: () => {
              // Remove only the deleted items from local arrays to preserve accessories
              this.shopping_cart.shopping_cart_items =
                this.shopping_cart.shopping_cart_items.filter(
                  (item: ShoppingCartItem) => !items_to_delete.includes(item.id as number)
                );
              this.shopping_items_price.shopping_cart_items =
                this.shopping_items_price.shopping_cart_items.filter(
                  (item: ShoppingCartItem) => !items_to_delete.includes(item.id as number)
                );
              if (CartInfo.cart_data) {
                CartInfo.cart_data = {
                  ...CartInfo.cart_data,
                  shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
                    (item: ShoppingCartItem) => !items_to_delete.includes(item.id as number)
                  )
                };
              }
              this.revisiting = false;
              this.in_cart_device = null;
              callback();
              this.modal_dismissed = true;
              this.modal_svc.dismissAll();
            },
            error: () => {}
          });
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  private seedSelectionsFromCart(cart?: ShoppingCartData): void {
    if (!cart) return;

    // Don't overwrite local cart if we have uncommitted changes (local items not in backend)
    // This prevents losing newly added items before they're saved to backend
    if (this.shopping_items_price.shopping_cart_items.length > 0) {
      const hasLocalChanges = this.shopping_items_price.shopping_cart_items.some(
        (localItem) =>
          !cart.shopping_cart_items.some(
            (backendItem) => backendItem.id === localItem.id && backendItem.type === localItem.type
          )
      );

      if (hasLocalChanges) {
        // Also check shopping_cart for local changes
        const cartHasLocalChanges = this.shopping_cart.shopping_cart_items.some(
          (localItem) =>
            !cart.shopping_cart_items.some(
              (backendItem) =>
                backendItem.id === localItem.id && backendItem.type === localItem.type
            )
        );

        if (cartHasLocalChanges) {
          return; // Skip overwriting both carts
        }
      }
    }

    this.shopping_cart = {
      ...cart,
      shopping_cart_items: [...cart.shopping_cart_items],
      // Preserve toggle value - don't overwrite from backend cart
      device_include_admin_options: this.shopping_cart.device_include_admin_options
    };

    // Safe to update from backend
    this.shopping_items_price.shopping_cart_items = [...cart.shopping_cart_items];
    this.shopping_items_price.keep_plan = cart.keep_plan;
    this.shopping_items_price.lineId = cart.lineId;
    this.shopping_items_price.existing_asset = cart.existing_asset;

    const cart_device: DeviceItemDevice | undefined =
      (cart.shopping_cart_items.find(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
      ) as DeviceItemDevice | undefined) ??
      (cart.existing_asset?.device as unknown as DeviceItemDevice | undefined);

    if (cart_device) {
      // cart_device.id can come back as a string (ShoppingCartItem.id: number | string)
      // while color/storage/name-override catalog lists always return id as a number.
      // Normalize once so the restored selection ids stay comparable (===) to those lists.
      const cart_device_id: number | undefined =
        cart_device.id !== undefined ? Number(cart_device.id) : undefined;

      // For BP devices, only restore color/storage/revisit state when the cart device
      // matches the newly selected device. Without this guard, switching from one BP device
      // (e.g. WM01) to another (e.g. WM03) would seed the old device's color ID into
      // loadColorList, causing it to pick a WM01 color variant from WM03's color list and
      // run the entire cascade (storage, name overrides) in the wrong BP context.
      const cart_matches_selection: boolean =
        !this.is_bp_device || cart_device_id === this.selected_device_id;

      if (cart_matches_selection) {
        // Seed the colour only while nothing is resolved yet. The cart device is a storage or
        // name-override variant, whose id is not in color_list, so re-seeding after the cascade
        // has picked a real colour_list entry leaves the swatch unhighlighted. Every
        // updateQueryParams re-emits queryParamMap and re-runs this method, so without the
        // guard the correct colour is overwritten moments after it is set.
        if (this.selected_color_device_id === -1)
          this.selected_color_device_id = cart_device_id ?? this.selected_color_device_id;
        this.selected_normalized_color =
          cart_device.original_color || cart_device.color || this.selected_normalized_color;
        this.selected_storage_device_id = cart_device_id ?? this.selected_storage_device_id;
        this.selected_storage_size = cart_device.size ?? this.selected_storage_size;
      }

      // For Order Type 3: Validate device belongs to current carrier before restoring
      let should_restore_device_id: boolean = true;
      let device_matches_carrier: boolean = true; // For non-Order Type 3, assume match

      if ([3].includes(this.current_activity_id)) {
        // If we have device_record loaded, verify device belongs to current carrier
        if (this.device_record?.length) {
          const current_carrier_device: DeviceItemData | undefined = this.device_record.find(
            (record: DeviceItemData) =>
              'carrier' in record && Number(record.carrier.id) === Number(this.selected_carrier_id)
          );
          device_matches_carrier =
            current_carrier_device?.devices?.some(
              (device: DeviceItemDevice) => device.id === cart_device_id
            ) ?? false;

          should_restore_device_id =
            device_matches_carrier && this.selected_name_override_id === -1;
        } else {
          should_restore_device_id = this.selected_name_override_id === -1;
        }
      }

      const has_restorable_cart_items: boolean = cart.shopping_cart_items.some(
        (item: ShoppingCartItem) => item.type !== CART_ITEM_TYPE_ACCESSORY
      );

      if (device_matches_carrier && cart_matches_selection && has_restorable_cart_items) {
        this.revisiting = true;
      }

      if (should_restore_device_id && cart_matches_selection) {
        this.selected_name_override_id = cart_device_id ?? this.selected_name_override_id;
      }
    }

    const cart_plan: ShoppingCartItem | undefined = cart.shopping_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
    );
    if (cart_plan) this.selected_plan_id = cart_plan.id as number;

    const cart_features: any = cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE
    );
    if (cart_features?.length) this.selected_feature_list = cart_features;
  }

  private selectCarrier(
    device_carrier: DeviceItemCarrier,
    preserve_selection: boolean = false
  ): void {
    const carrier_id: number = Number(device_carrier.id);

    if ([3].includes(this.current_activity_id)) {
      // If preserve_selection is true, we're coming from loadCarrierList after initial load
      if (preserve_selection) {
        this.selected_carrier_id = carrier_id;
        // Toggle false → true so ngOnChanges fires on every call, including repeated admin
        // toggles where load_color_list is already true from the previous cycle.
        this.load_color_list = false;
        setTimeout(() => (this.load_color_list = true));
        return;
      }

      // Detect carrier change to clear stale cart items
      const previous_carrier_id: number = this.selected_carrier_id;
      const carrier_changed: boolean = previous_carrier_id !== carrier_id;
      if (!carrier_changed) return;

      // Update carrier ID first
      this.selected_carrier_id = carrier_id;

      // Clear plans/features but preserve device info
      this.resetPlansAndFeatures();

      // If carrier changed, clear cart items (except accessories) and reset revisiting flag
      if (carrier_changed) {
        this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
          (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
        );
        this.revisiting = false; // Force fresh load instead of restoration
        this.has_entered_admin_mode = false;
      }

      // Update query params with new carrier
      this.updateQueryParams();

      // Reload device with new carrier context
      this.loadDevice(() => {
        // Find device matching the new carrier
        if (this.device_record?.length) {
          const carrier_device_record = this.device_record.find(
            (record) =>
              'carrier' in record && Number(record.carrier.id) === Number(this.selected_carrier_id)
          );

          if (carrier_device_record?.devices?.length) {
            this.active_device = carrier_device_record.devices[0];
            this.selected_device_id = this.active_device.id;
            this.selected_name_override_id = -1; // Reset to allow fresh selection
          }
        }

        // Emit event to reload name overrides
        this.carrierChanged.emit();
      });

      return;
    }

    if (preserve_selection || (this.revisiting && carrier_id === this.selected_carrier_id)) {
      this.selected_carrier_id = carrier_id;
      // Toggle flag to force Angular change detection even if already true
      this.load_color_list = false;
      setTimeout(() => {
        this.load_color_list = true;
      });
      return; // Exit early after setting flag to trigger child component cascade
    }

    if (this.selected_carrier_id === carrier_id) return;

    this.resetPlansAndFeatures();
    this.has_entered_admin_mode = false;

    this.reset_list_options = true;
    this.selected_color_device_id = -1;
    this.selected_storage_device_id = -1;
    this.selected_storage_size = '';
    this.selected_name_override_id = -1;
    this.selected_normalized_color = '';
    this.selected_device_id = -1;
    this.selected_storage_device_price = {
      onetime: { cost_to_currency: '' },
      monthly: { cost_to_currency: '' }
    };
    this.active_device = null;
    this.selected_carrier_id = carrier_id;

    // Load devices for new carrier, then trigger color cascade
    this.loadDevice(() => {
      this.load_color_list = true;
      this.updateQueryParams();
    });
  }

  private generateShoppingItems(
    new_cart_item: any,
    new_cart_type: string,
    refresh: boolean = false,
    category_id?: number
  ): void {
    // Check ONLY local cart - don't check CartInfo.cart_data here because we might be restoring FROM it
    const item_already_in_cart: boolean = !!this.shopping_cart.shopping_cart_items.find(
      (item: ShoppingCartItem) => item.id == new_cart_item.id
    );

    if (
      item_already_in_cart &&
      !new_cart_item.included &&
      !new_cart_item.unchanged &&
      new_cart_type === CART_ITEM_TYPE_FEATURE
    ) {
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.id != new_cart_item.id
      );
      this.shopping_items_price.shopping_cart_items =
        this.shopping_items_price.shopping_cart_items.filter(
          (item: ShoppingCartItem) => item.id != new_cart_item.id
        );

      // Mirror feature removal into CartInfo so stepper navigation preserves current selections.
      if (CartInfo.cart_data) {
        CartInfo.cart_data = {
          ...CartInfo.cart_data,
          shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
            (item: ShoppingCartItem) =>
              !(item.id == new_cart_item.id && item.type === CART_ITEM_TYPE_FEATURE)
          )
        };
      }
    } else {
      if (category_id) new_cart_item.category_id = category_id;

      // Ensure quantity is set for devices (devices don't have quantity property)
      if (new_cart_type === CART_ITEM_TYPE_DEVICE && !new_cart_item.quantity) {
        new_cart_item.quantity = 1;
      }

      if (new_cart_type === CART_ITEM_TYPE_DEVICE && this.is_bp_device && this.active_device)
        new_cart_item.business_purpose = (
          this.active_device as DeviceItemDeviceBP
        ).business_purpose;

      const cart_item_to_push: ShoppingCartItem = createShoppingObject(
        new_cart_item,
        new_cart_type,
        this.keep_plan ? this.existing_image_url : this.currentCarrierImageURL(),
        this.is_bp_device
      );

      this.shopping_cart.shopping_cart_items.push(cart_item_to_push);
      this.shopping_items_price.shopping_cart_items.push(
        createShoppingPriceObject(new_cart_item, new_cart_type)
      );
    }

    this.shopping_cart.carrier = this.carrier_list.find(
      (carrier: DeviceItemCarrier) => carrier.id === this.selected_carrier_id
    );

    this.createPayloadString();
    // Always refresh when device is added/changed to update one-time cost
    const should_refresh: boolean = refresh || new_cart_type === CART_ITEM_TYPE_DEVICE;
    this.refresh_summary.next(should_refresh);
  }

  private displayOldPlanAndFeatures(shopping_cart: ShoppingCartData): void {
    this.shopping_cart.keep_plan = this.shopping_items_price.keep_plan = this.keep_plan;

    const selected_cart_items: ShoppingCartItem[] = shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) =>
        item.type === CART_ITEM_TYPE_DEVICE || item.type === CART_ITEM_TYPE_ACCESSORY
    )!;

    if (selected_cart_items.length > 0)
      selected_cart_items.forEach((item: ShoppingCartItem) => {
        if (!this.getItemById(item.id))
          this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id);
      });

    if (this.keep_plan) {
      this.shopping_svc.preselected_item = true;

      const businessPlan = shopping_cart.existing_asset?.line?.business_plan;
      const linePlan = shopping_cart.existing_asset?.line?.plan;

      let old_plan_and_features: PlanType | LinePlan | undefined =
        businessPlan && 'full_description' in businessPlan ? (businessPlan as PlanType) : linePlan;

      this.existing_image_url = this.currentCarrierImageURL();

      if (old_plan_and_features?.id) {
        const isPlanType = (plan: any): plan is PlanType => 'full_description' in plan;

        this.plan_list = [
          {
            name: isPlanType(old_plan_and_features)
              ? (old_plan_and_features.name_override ?? old_plan_and_features.name)
              : old_plan_and_features.name,

            id: old_plan_and_features?.id,
            carrier: { image_url: shopping_cart.existing_asset?.line?.carrier_image_url },
            cost_to_currency: old_plan_and_features?.cost_to_currency,
            full_description: old_plan_and_features?.full_description,
            description: old_plan_and_features?.description
          }
        ] as PlanType[];

        // There will only ever be one plan when we "keep plans and features"
        this.selectPlan(this.plan_list[0]);

        this.selected_plan_id = old_plan_and_features?.id as number;

        if (businessPlan?.features?.length)
          this.feature_list = businessPlan.features as FeatureType[];

        // For multiple features
        if (this.feature_list)
          for (const feature_details of this.feature_list) this.selectFeature(feature_details);

        this.no_features = this.feature_list?.length ? false : true;
      } else this.no_plans = true;
    } else {
      // Add plan and not-included features
      const plan_and_features: ShoppingCartItem[] = shopping_cart.shopping_cart_items.filter(
        (item: ShoppingCartItem) =>
          item.type === CART_ITEM_TYPE_PLAN ||
          (item.type === CART_ITEM_TYPE_FEATURE && !item.additional_info?.included)
      )!;

      if (plan_and_features.length > 0)
        plan_and_features.forEach(
          (item: ShoppingCartItem) =>
            !this.getItemById(item.id) &&
            this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id)
        );
    }
  }

  private currentCarrierImageURL(): string {
    return (
      this.carrier_list.find(
        (carrier: DeviceItemCarrier) => carrier.id === this.selected_carrier_id
      )?.image_url ?? ''
    );
  }

  private createPayloadString(): void {
    this.shopping_cart.shopping_cart_items.map(
      (item) => (item.edit_link_payload = JSON.stringify(this.generateQueryParams()))
    );
  }

  private updateQueryParams(): void {
    const params: StoreQueryParams = this.generateQueryParams();

    this.router
      .navigate([], {
        relativeTo: this.activated_route,
        queryParams: params,
        replaceUrl: true,
        queryParamsHandling: 'merge'
      })
      .then(() => {
        this.activity_state_svc.setSteps(this.progress_bar_config);
      });
  }

  private removeItems(type: string): void {
    // Create new arrays to ensure no shared references
    const filtered_cart = this.shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type != type
    );
    const filtered_price = [...this.shopping_items_price.shopping_cart_items].filter(
      (item: ShoppingCartItem) => item.type != type
    );

    this.shopping_cart.shopping_cart_items = [...filtered_cart];
    this.shopping_items_price.shopping_cart_items = [...filtered_price];
  }

  private syncPriceItemsWithCart(): void {
    const cart_items: ShoppingCartItem[] = this.shopping_cart.shopping_cart_items;

    this.shopping_items_price.shopping_cart_items =
      this.shopping_items_price.shopping_cart_items.filter((price_item: ShoppingCartItem) =>
        cart_items.some(
          (cart_item: ShoppingCartItem) =>
            cart_item.id == price_item.id && cart_item.type === price_item.type
        )
      );

    cart_items.forEach((cart_item: ShoppingCartItem) => {
      const present_in_price_payload: boolean = this.shopping_items_price.shopping_cart_items.some(
        (price_item: ShoppingCartItem) =>
          price_item.id == cart_item.id && price_item.type === cart_item.type
      );

      if (!present_in_price_payload) {
        this.shopping_items_price.shopping_cart_items.push({
          type: cart_item.type,
          id: cart_item.id,
          quantity: cart_item.quantity ?? 1,
          sub_type: cart_item.sub_type
        } as ShoppingCartItem);
      }
    });
  }

  private keepPlansAndFeatures(): void {
    if (this.plan_list && this.plan_list.length > 0) {
      this.selectPlan(this.plan_list[0]);
      this.feature_list.forEach((feature: FeatureType) => this.selectFeature(feature));
    }
    this.device_plan.deviceId = this.selected_device_id;
  }

  // TODO: Verify if this is really needed
  private resetPlanSelection(refresh: boolean = false): void {
    // Don't reset plans during backward navigation for Order Type 3
    if ([3].includes(this.current_activity_id) && (this.revisiting || this.keep_plan)) return;

    this.selected_plan_id = 0;
    this.feature_list = this.selected_feature_list = [];
    this.removeItems(CART_ITEM_TYPE_PLAN);
    this.removeItems(CART_ITEM_TYPE_FEATURE);
    if (CartInfo.cart_data) {
      CartInfo.cart_data = {
        ...CartInfo.cart_data,
        shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
          (item: ShoppingCartItem) =>
            item.type !== CART_ITEM_TYPE_PLAN && item.type !== CART_ITEM_TYPE_FEATURE
        )
      };
    }
    if (refresh) this.refresh_summary.next(true);
  }

  private retrieveCartObjects(device_details?: DeviceItemDevice): InCartObjects {
    const all_cart_items: ShoppingCartItem[] = [
      ...(this.shopping_cart?.shopping_cart_items ?? []),
      ...(CartInfo.cart_data?.shopping_cart_items ?? [])
    ];

    // Deduplicate by id to avoid duplicates from merging both arrays
    const unique_cart_items = all_cart_items.reduce(
      (acc: ShoppingCartItem[], item: ShoppingCartItem) => {
        if (
          !acc.find(
            (existing: ShoppingCartItem) => existing.id == item.id && existing.type === item.type
          )
        ) {
          acc.push(item);
        }
        return acc;
      },
      []
    );

    const device_item: ShoppingCartItem | undefined = unique_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
    );
    const plan_item: ShoppingCartItem | undefined = unique_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
    );
    const feature_items: ShoppingCartItem[] = unique_cart_items.filter(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE
    ) as ShoppingCartItem[];

    const resolved_device_id: number | string | undefined =
      [3].includes(this.current_activity_id) && this.revisiting
        ? device_item?.id
        : device_details?.id && device_details.id !== device_item?.id
          ? device_details.id
          : device_item?.id;
    const device_id: number = resolved_device_id !== undefined ? Number(resolved_device_id) : -1;

    const plan_id: number =
      plan_item?.id !== undefined &&
      device_id !== -1 &&
      (this.selected_device_id === undefined || this.selected_device_id === device_id)
        ? Number(plan_item.id)
        : Number(this.selected_plan_id);

    return {
      device_id: device_id,
      plan_id: plan_id,
      features: feature_items as []
    };
  }

  private generateQueryParams(): StoreQueryParams {
    // For Order Type 3: Prioritize selected_name_override_id or active_device after name override selection
    // This ensures deviceId updates correctly when carrier changes
    let device_id_for_params: number | undefined;
    if ([3].includes(this.activity_state_svc.orderTypeId())) {
      // Order Type 3: Use name override selection or active device
      device_id_for_params =
        (this.selected_name_override_id !== -1 ? this.selected_name_override_id : undefined) ??
        this.active_device?.id ??
        this.selected_device_id;
    } else {
      // Other order types: Use standard fallback chain
      device_id_for_params =
        this.in_cart_device?.id ??
        (this.getItemByType(CART_ITEM_TYPE_DEVICE)?.id as number) ??
        this.selected_device_id;
    }

    return {
      carrierId: this.selected_carrier_id,
      name: this.selected_device_name,
      filterConfigId: this.devices_payload.filters[0].id,
      isBpDevice: this.is_bp_device ?? undefined,
      filters:
        [3].includes(this.activity_state_svc.orderTypeId()) || this.is_bp_device
          ? this.url_filters_param
          : undefined,
      categories: JSON.stringify(this.devices_payload.categories),
      countryId: this.devices_payload.countryId,
      deviceId: device_id_for_params,
      number_of_plans: this.plan_list.length,
      number_of_features: this.feature_list.length
    };
  }

  private setCartValues(shopping_cart: ShoppingCartData): void {
    this.shopping_cart.order_for_person = shopping_cart.order_for_person;
    this.shopping_cart.fields = shopping_cart.fields;
    this.shopping_cart.device_identifier = shopping_cart.device_identifier;
    this.shopping_cart.order_sim = shopping_cart.order_sim;
    this.shopping_cart.new_sim = shopping_cart.new_sim;
    this.shopping_cart.esim_activation = shopping_cart.esim_activation;
    this.shopping_cart.new_eid = shopping_cart.new_eid;
    this.shopping_cart.device_include_admin_options = shopping_cart.device_include_admin_options;
    this.shopping_items_price.device_include_admin_options =
      shopping_cart.device_include_admin_options;
    this.shopping_cart.accessories_include_admin_options =
      shopping_cart.accessories_include_admin_options;
    this.shopping_cart.order_type_settings = shopping_cart.order_type_settings;
    this.shopping_cart.quantity = shopping_cart.quantity;
    this.shopping_cart.add_additional_line = shopping_cart.add_additional_line;

    if (shopping_cart.orderTypeId === 3)
      this.shopping_cart.existing_asset = shopping_cart.existing_asset;
  }

  private resetPlansAndFeatures(): void {
    this.shopping_svc.preselected_item = false;
    this.selected_storage_size = '';
    this.selected_name_override_id = -1;
    this.admin_override_persist = false;

    if (!this.keep_plan) {
      this.plan_list = this.feature_list = this.selected_feature_list = [];
      this.selected_plan_id = 0;
      this.no_plans = this.no_features = false;
    }

    this.removeItems(CART_ITEM_TYPE_DEVICE);
    this.removeItems(CART_ITEM_TYPE_PLAN);
    this.removeItems(CART_ITEM_TYPE_FEATURE);
    if (CartInfo.cart_data) {
      CartInfo.cart_data = {
        ...CartInfo.cart_data,
        shopping_cart_items: CartInfo.cart_data.shopping_cart_items.filter(
          (item: ShoppingCartItem) =>
            item.type !== CART_ITEM_TYPE_PLAN &&
            item.type !== CART_ITEM_TYPE_FEATURE &&
            item.type !== CART_ITEM_TYPE_DEVICE
        )
      };
    }

    if (
      this.shopping_cart.shopping_cart_items.filter(
        (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_ACCESSORY
      ).length > 0
    ) {
      this.is_display_one_time_cost = true;
      this.is_display_recurring_cost = false;
    } else this.is_display_one_time_cost = this.is_display_recurring_cost = false;

    this.device_plan.deviceId = null;
    this.plan_features_sub.next();
    this.refresh_summary.next(true);
  }
}
